package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import kotlin.random.Random

/**
 * Two versions of one string, and a choice between them that cannot lose either.
 *
 * The last of the seven components §2 authorised, and the one whose case rested least on repetition:
 * it had two call sites, not three. It earns its place on the other bar — **focus order, a
 * live-region announcement and the ARIA of a destructive two-column choice are behaviour that cannot
 * live in a page and be got right twice.** It was living in a page, and two of those three were
 * missing.
 *
 * What shipped in the editor composed `LCard`, `LAlert` and `LButton` correctly — so the answer to
 * the brief's standing **UNVERIFIED** ("whether it is one-off page CSS or composed from existing DS
 * primitives") is: composed, plus four editor-local styles. It was not the styling that needed
 * promoting. It was this:
 *
 * - **The alert never announced.** `LAlert` takes `live`, and the conflict card did not pass it, so
 *   a conflict that appeared without moving focus was silent. The editor happens to move focus to the
 *   card, which announces it there; the review lane does not, and that is the caller this component
 *   was promoted ahead of.
 * - **Neither version was labelled programmatically.** "Yours" and the text under it were two
 *   unrelated runs in the accessibility tree, so the one question the card exists to answer — *which
 *   of these is mine?* — was the one thing a screen reader could not report. Each version is now a
 *   labelled group.
 *
 * The values and the actions are slots rather than strings. The editor renders its versions through
 * `PlaceholderText`, so `{count}` is visibly a placeholder and not prose, and its three actions carry
 * editor state; the review lane needs neither. A component that took strings would have forced the
 * editor to give that up, and a component that took editor types could not have moved here at all.
 */
val LConflictCardStyle = CssStyle.base {
    Modifier.width(100.percent).gap(Space.XS)
}

/**
 * One version's surface: a sunken well, ruled.
 *
 * Reading size and a 1.65 line-height because this is the one place in the product where somebody
 * compares two pieces of translated text word by word under time pressure. `overflow-wrap: anywhere`
 * because a translation can be one unbroken 200-character token in a language that does not space
 * its words, and a card that overflows its own column is a card that hides the choice.
 */
val LConflictVersionStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Control)
        .padding(Space.LG)
        .gap(Space.MD)
        .fontSize(TypeScale.Reading)
        .lineHeight(1.65)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("overflow-wrap", "anywhere")
        }
}

val LConflictLabelStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .styleModifier {
            property("letter-spacing", "0")
            property("text-transform", "uppercase")
        }
}

@Composable
fun LConflictCard(
    title: String,
    description: String,
    mineLabel: String,
    theirsLabel: String,
    modifier: Modifier = Modifier,
    /**
     * Whether the alert announces itself.
     *
     * Default true, because a conflict appears without being asked for and the person it concerns may
     * not be looking at it. **Pass false when the caller moves focus to the card instead** — the
     * editor does, and a live region on top of a focus move announces the same thing twice, which
     * reads as a stutter rather than as emphasis.
     */
    announce: Boolean = true,
    /** The focus target, when a caller wants to move focus here. Sets `tabindex="-1"` with it. */
    focusId: String? = null,
    mine: @Composable () -> Unit,
    theirs: @Composable () -> Unit,
    actions: @Composable () -> Unit,
) {
    // Two ids per card, so two cards on one page cannot label each other's versions.
    val base = remember { "lingvo-conflict-${Random.nextInt(1_000_000)}" }

    LCard(
        Modifier
            .fillMaxWidth()
            .then(
                if (focusId != null) {
                    Modifier.attr("id", focusId).attr("tabindex", "-1")
                } else {
                    Modifier
                },
            )
            .attr("data-conflict", "true")
            .then(modifier),
    ) {
        Column(LConflictCardStyle.toModifier()) {
            LAlert(title, description, variant = LAlertVariant.WARNING, live = announce)

            Version("$base-mine", mineLabel, mine)
            Version("$base-theirs", theirsLabel, theirs)

            // Wrapping, because three actions plus a long verb do not fit the editor's utility
            // column at its narrowest and a button that leaves the card is a choice nobody can make.
            Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap)) { actions() }
        }
    }
}

/**
 * A labelled version.
 *
 * `role="group"` with `aria-labelledby` rather than a bare eyebrow above some text: without it the
 * label and the value are two unrelated runs, and "Yours" is read as though it were the start of the
 * translation rather than the name of it.
 */
@Composable
private fun Version(labelId: String, label: String, value: @Composable () -> Unit) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(Space.XS)
            .attr("role", "group")
            .attr("aria-labelledby", labelId),
    ) {
        Div(LConflictLabelStyle.toModifier().attr("id", labelId).toAttrs()) { Text(label) }
        Div(LConflictVersionStyle.toModifier().fillMaxWidth().toAttrs()) { value() }
    }
}
